/**
 * Toile's facets (`@core/wall/facets`): its own use (what the picture is made to be) and subject,
 * then season and colour. Style alone is decided per cell.
 */
import {COLOUR, SEASON, type HouseFacets} from '@core/wall/facets'

export const TOILE_FACETS: HouseFacets = [
    {
        key: 'use',
        label: 'Use',
        required: true,
        values: [
            {id: 'coloring', label: 'Coloring & activities', short: 'coloring page', meta: 'Coloring pages, mazes, dot-to-dot, tracing'},
            {id: 'classroom', label: 'Classroom', short: 'classroom', meta: 'Worksheets, charts, flashcards, classroom decor'},
            {id: 'cards', label: 'Cards & invitations', short: 'cards', meta: 'Greeting cards, invitations, announcements, tags'},
            {id: 'wall-art', label: 'Posters & wall art', short: 'wall art', meta: 'Prints, posters, banners, signs to hang'},
            {id: 'screens', label: 'Wallpapers & screens', short: 'wallpaper', meta: 'Phone wallpapers, backgrounds, icons'},
            {id: 'stickers', label: 'Stickers & labels', short: 'stickers', meta: 'Sticker sheets, labels, decals'},
            {id: 'body-art', label: 'Tattoo & mehndi', short: 'tattoo', meta: 'Tattoo designs, flash, mehndi, face paint'},
            {id: 'patterns', label: 'Patterns & textiles', short: 'patterns', meta: 'Repeats, prints, textile traditions'},
            {id: 'planners', label: 'Planners & trackers', short: 'planners', meta: 'Planners, calendars, trackers, journal pages'},
            {id: 'crafts', label: 'Crafts & templates', short: 'crafts', meta: 'Templates, stitch charts, cut files, paper crafts'},
            {id: 'references', label: 'References & how-to', short: 'references', meta: 'Drawing guides, pose and anatomy references, diagrams'},
            {id: 'business', label: 'Logos & business', short: 'logos', meta: 'Logos, packaging, menus, shop signs, merch'},
            {id: 'social', label: 'Social & creator', short: 'social', meta: 'Posts, thumbnails, covers, avatars'},
            {id: 'interiors', label: 'Interiors & home', short: 'interiors', meta: 'Room designs, layouts, gardens'},
            {id: 'beauty', label: 'Nails, hair & makeup', short: 'beauty', meta: 'Nail designs, hairstyles, makeup looks'},
            {id: 'art-styles', label: 'Illustration & art styles', short: 'art styles', meta: 'A subject or a movement drawn as art'},
            {id: 'games', label: 'Games & tabletop', short: 'games', meta: 'RPG maps and tokens, playing cards, party games'},
            {id: 'food', label: 'Cakes & food', short: 'cakes', meta: 'Cakes, cookies, food and drink pictures'},
        ],
    },
    {
        key: 'subject',
        label: 'Subject',
        required: false,
        values: [
            {id: 'animals', label: 'Animals', short: 'animals', meta: 'Pets, wildlife, birds, insects, sea life'},
            {id: 'plants', label: 'Flowers & plants', short: 'flowers', meta: 'Flowers, leaves, trees, gardens'},
            {id: 'people', label: 'People & characters', short: 'people', meta: 'People, faces, figures, characters'},
            {id: 'food', label: 'Food & drink', short: 'food', meta: 'Dishes, sweets, drinks'},
            {id: 'places', label: 'Places & buildings', short: 'places', meta: 'Houses, cities, rooms, maps'},
            {id: 'vehicles', label: 'Vehicles', short: 'vehicles', meta: 'Cars, trucks, planes, boats, bikes'},
            {id: 'fantasy', label: 'Fantasy & creatures', short: 'fantasy', meta: 'Dragons, unicorns, monsters, magic'},
            {id: 'faith', label: 'Faith & spirit', short: 'faith', meta: 'Sacred figures, symbols, scripture'},
            {id: 'lettering', label: 'Letters & numbers', short: 'letters', meta: 'Alphabets, words, quotes, numbers'},
            {id: 'abstract', label: 'Shapes & ornament', short: 'shapes', meta: 'Geometric, abstract, ornamental motifs'},
            {id: 'nature', label: 'Sky & landscape', short: 'landscape', meta: 'Landscapes, sea, sky, space, weather'},
            {id: 'objects', label: 'Everyday objects', short: 'objects', meta: 'Tools, toys, clothes, things'},
        ],
    },
    SEASON,
    COLOUR,
    // Decided per cell: a pack crosses three readings of its subject with three styles. The pack's
    // style labels are free text (2,890 distinct); each is classified once into this register.
    {
        key: 'style',
        label: 'Style',
        required: false,
        level: 'tile',
        values: [
            {id: 'line-art', label: 'Line art', short: 'line art', meta: 'Black outline, fine line, clean contour, blackline'},
            {id: 'ink', label: 'Ink & dotwork', short: 'ink', meta: 'Ink, dotwork, stipple, linocut, engraving'},
            {id: 'sketch', label: 'Pencil & sketch', short: 'sketch', meta: 'Pencil, charcoal, crayon, hand-drawn, doodle'},
            {id: 'watercolor', label: 'Watercolor', short: 'watercolor', meta: 'Watercolor, wash, gouache, loose paint'},
            {id: 'painted', label: 'Painted', short: 'painted', meta: 'Oil, acrylic, painted print, impasto'},
            {id: 'pastel', label: 'Soft pastel', short: 'pastel', meta: 'Pastel, soft and muted colour'},
            {id: 'flat', label: 'Flat & vector', short: 'flat', meta: 'Flat colour, vector, bright flat graphics'},
            {id: 'cartoon', label: 'Cartoon & kawaii', short: 'cartoon', meta: "Cartoon, kawaii, storybook, kids' style"},
            {id: 'minimal', label: 'Minimal', short: 'minimal', meta: 'Few lines, much space, modern minimal'},
            {id: 'vintage', label: 'Vintage & retro', short: 'vintage', meta: 'Retro, classic, art deco, period print'},
            {id: 'poster', label: 'Poster', short: 'poster', meta: 'Screenprint and WPA-style posters, bold graphic prints'},
            {id: 'paper', label: 'Paper & collage', short: 'paper', meta: 'Paper cut, collage, cut-out, mosaic'},
            {id: 'render3d', label: '3D & clay', short: '3d', meta: '3D render, clay, vinyl, sculpted, glossy'},
            {id: 'ornamental', label: 'Ornamental & foil', short: 'ornamental', meta: 'Henna, folk, patterned, gold foil, gothic ornament'},
            {id: 'technical', label: 'Technical & chalk', short: 'technical', meta: 'Blueprint, graph paper, chart, chalkboard, diagram'},
        ],
    },
]
